import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AvailabilityBadge, SeatsBadge } from '../components/ui/status-badge';
import { OfferBanner } from '../components/OfferBanner';
import { SaleOfferBanner, SaleOfferTag } from '../components/SaleOfferBanner';
import axios from 'axios';
import {
  MapPin,
  Users,
  Clock,
  ArrowRight,
  ChevronRight,
  Monitor,
  Loader2,
  ArrowLeft,
  Phone,
  Mail,
  Sparkles,
  Info,
  TrainFront,
  Zap,
  Wifi,
  Car,
  Coffee,
  Printer,
  Snowflake,
  ShieldCheck,
  Dot,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Amenity strings are free text entered by admins, so match on keywords rather
// than an exact lookup. Falls back to a neutral dot for anything unrecognised.
const AMENITY_ICONS = [
  [/metro|transit|station/i, TrainFront],
  [/power|backup|generator|ups/i, Zap],
  [/wifi|internet|broadband/i, Wifi],
  [/park/i, Car],
  [/cafe|coffee|pantry|kitchen/i, Coffee],
  [/print|scan|copier/i, Printer],
  [/\bac\b|air.?condition|hvac/i, Snowflake],
  [/security|cctv|guard/i, ShieldCheck],
];

const iconForAmenity = (amenity) => {
  const match = AMENITY_ICONS.find(([pattern]) => pattern.test(amenity));
  return match ? match[1] : Dot;
};

// The three-up highlight row under a venue name: seats, metro distance, then
// the most useful remaining amenity. Falls back to opening hours when a venue
// has none of these recorded, so the row is never empty.
const venueHighlights = (venue) => {
  const items = [];

  if (venue.total_capacity > 0) {
    items.push({ icon: Users, label: `${venue.total_capacity} Seats` });
  }
  if (venue.metro_distance) {
    items.push({ icon: TrainFront, label: `Metro ${venue.metro_distance}` });
  }

  const amenities = venue.amenities || [];
  const preferred = amenities.find(a => /power|backup/i.test(a)) || amenities[0];
  if (preferred && items.length < 3) {
    items.push({ icon: iconForAmenity(preferred), label: preferred });
  }

  if (items.length === 0) {
    items.push({
      icon: Clock,
      label: `${venue.opening_time || '08:00'} - ${venue.closing_time || '22:00'}`,
    });
  }
  return items;
};

const RATE_UNIT = { daily: '/day', monthly: '/month', hourly: '/hr' };

const formatRate = (rate) => `₹${Number(rate || 0).toLocaleString('en-IN')}`;

const PageHeader = ({ children }) => (
  <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
    <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
      {children}
    </div>
  </header>
);

const ExplorePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [venueSpaces, setVenueSpaces] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSpaces, setLoadingSpaces] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/venues`)
      .then(res => setVenues(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Deep-link support: /explore?venue_id=xxx opens straight to that venue's
  // spaces so users can pick a room before ever being asked to log in.
  useEffect(() => {
    const preVenueId = searchParams.get('venue_id') || searchParams.get('venue');
    if (!preVenueId || venues.length === 0 || selectedVenue) return;
    const match = venues.find(v => v.id === preVenueId);
    if (match) handleSelectVenue(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, searchParams]);

  const handleSelectVenue = async (venue) => {
    setSelectedVenue(venue);
    setLoadingSpaces(true);
    try {
      const res = await axios.get(`${API}/public/venues/${venue.id}/spaces`);
      setVenueSpaces(res.data);
    } catch {
      setVenueSpaces(null);
    } finally {
      setLoadingSpaces(false);
    }
  };

  const handleBookNow = (space) => {
    const bookingPath = `/booking?venue_id=${selectedVenue.id}&bookable_id=${space.id}`;
    if (isAuthenticated) {
      navigate(bookingPath);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(bookingPath)}`);
    }
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'daily': return 'Day Pass';
      case 'monthly': return 'Monthly';
      default: return 'Hourly';
    }
  };

  // "From" price for a venue card — the cheapest daily rate (the headline
  // product), falling back to hourly then monthly.
  const venueFromRate = (venue) => {
    const rates = venue.min_rate_by_mode || {};
    const mode = ['daily', 'hourly', 'monthly'].find(m => rates[m] > 0);
    return mode ? { rate: rates[mode], unit: RATE_UNIT[mode] } : null;
  };

  const activeVenues = useMemo(
    () => venues.filter(v => v.is_active !== false),
    [venues]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Venue spaces view
  if (selectedVenue && venueSpaces) {
    const venue = venueSpaces.venue;
    const spaces = venueSpaces.spaces;

    return (
      <div className="min-h-screen bg-background" data-testid="explore-spaces-page">
        <PageHeader>
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => { setSelectedVenue(null); setVenueSpaces(null); }}
              className="p-2 rounded-lg hover:bg-muted transition-colors shrink-0"
              data-testid="back-to-venues"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="font-heading text-lg font-bold truncate">{venue.name}</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 text-primary shrink-0" />{venue.address || venue.city}
              </p>
            </div>
          </div>
          {isAuthenticated ? (
            <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm">Dashboard</Button>
          ) : (
            <Button onClick={() => navigate('/login')} variant="outline" size="sm">Login</Button>
          )}
        </PageHeader>

        <div className="max-w-6xl mx-auto px-4 py-8">
          <SaleOfferBanner />
          <OfferBanner venue={venue} />

          {/* Venue Info */}
          <div className="flex flex-wrap gap-4 mb-8 text-sm text-muted-foreground">
            {venue.phone && (
              <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" />{venue.phone}</span>
            )}
            {venue.email && (
              <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" />{venue.email}</span>
            )}
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{venue.opening_time} - {venue.closing_time}</span>
          </div>

          <h2 className="font-heading text-xl font-semibold mb-6">Available Spaces ({spaces.length})</h2>

          {loadingSpaces ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : spaces.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">No spaces available at this venue</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              {spaces.map(space => (
                <Card key={space.id} className="group overflow-hidden flex flex-col h-full hover:shadow-lg transition-shadow duration-300" data-testid={`space-card-${space.id}`}>
                  {/* Image */}
                  <div className="h-44 bg-muted relative overflow-hidden shrink-0">
                    {space.images?.[0] ? (
                      <img src={space.images[0].startsWith('/') ? `${BACKEND_URL}${space.images[0]}` : space.images[0]} alt={space.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/5">
                        <Monitor className="w-12 h-12 text-primary/20" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <AvailabilityBadge status="available" />
                      {space.offer_enabled && (
                        <Badge
                          variant="default"
                          shape="pill"
                          className="px-2.5 py-1 text-[11px]"
                          data-testid={`space-offer-badge-${space.id}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />{space.offer_badge || 'Offer'}
                        </Badge>
                      )}
                    </div>
                    <SeatsBadge count={space.capacity} className="absolute top-3 right-3" />
                  </div>

                  {/* gap-3 rather than space-y-3: the latter's `> * + *` rule
                      outranks the price row's mt-auto and breaks bottom-pinning. */}
                  <CardContent className="p-5 flex flex-col flex-1 gap-3">
                    <OfferBanner venue={space} compact />
                    <SaleOfferTag bookableType={space.bookable_type} />

                    <div>
                      <h3 className="font-heading font-bold text-lg flex items-center gap-1.5">
                        {space.name}
                        {space.description && (
                          <Info className="w-4 h-4 text-muted-foreground shrink-0" title={space.description} />
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{getModeLabel(space.booking_mode)}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4" />{space.capacity} {space.capacity === 1 ? 'Seat' : 'Seats'}
                      </span>
                      {space.booking_mode === 'hourly' && space.min_booking_hours > 1 && (
                        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />Min {space.min_booking_hours}h</span>
                      )}
                      {space.amenities?.slice(0, 2).map((a, i) => {
                        const Icon = iconForAmenity(a);
                        return (
                          <span key={i} className="flex items-center gap-1.5"><Icon className="w-4 h-4" />{a}</span>
                        );
                      })}
                    </div>

                    {/* Price + Book */}
                    <div className="flex items-center justify-between gap-3 mt-auto pt-1">
                      <div>
                        <span className="text-xl font-bold">
                          {formatRate(space.hourly_rate)}
                          <span className="text-sm font-normal text-muted-foreground ml-0.5">
                            {RATE_UNIT[space.booking_mode] || RATE_UNIT.hourly}
                          </span>
                        </span>
                        <p className="text-[10px] text-muted-foreground">+ 18% GST</p>
                      </div>
                      <Button onClick={() => handleBookNow(space)} data-testid={`book-now-${space.id}`}>
                        Book Now <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Venues list view
  return (
    <div className="min-h-screen bg-background" data-testid="explore-venues-page">
      <PageHeader>
        <div className="flex items-center gap-3">
          <img src="/logo.webp" alt="Clayworks" className="h-8 w-auto" />
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isAuthenticated ? (
            <Button onClick={() => navigate('/dashboard')} size="sm">Dashboard</Button>
          ) : (
            <>
              <Button onClick={() => navigate('/login')} variant="outline" size="sm">Login</Button>
              <Button onClick={() => navigate('/register')} size="sm">Sign Up</Button>
            </>
          )}
        </div>
      </PageHeader>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <SaleOfferBanner />
        <div className="text-center mb-10">
          <p className="text-sm tracking-[0.15em] text-muted-foreground uppercase mb-3">
            Available Co-working Spaces
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">
            Choose from our premium locations
          </h1>
        </div>

        {/* Venues Grid */}
        {activeVenues.length === 0 ? (
          <div className="text-center py-16" data-testid="explore-no-results">
            <p className="text-muted-foreground">No centers are available right now.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-[30px]">
            {activeVenues.map(venue => {
              const fromRate = venueFromRate(venue);
              return (
                <Card
                  key={venue.id}
                  className="group cursor-pointer overflow-hidden flex flex-col h-full hover:shadow-lg transition-shadow duration-300"
                  onClick={() => handleSelectVenue(venue)}
                  data-testid={`venue-card-${venue.id}`}
                >
                  <div className="h-48 bg-muted relative overflow-hidden shrink-0">
                    {venue.images?.[0] ? (
                      <img src={venue.images[0].startsWith('/') ? `${BACKEND_URL}${venue.images[0]}` : venue.images[0]} alt={venue.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                        <MapPin className="w-12 h-12 text-primary/30" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <AvailabilityBadge status={venue.space_count > 0 ? 'available' : undefined} />
                      {venue.offer_enabled && (
                        <Badge
                          variant="default"
                          shape="pill"
                          className="px-2.5 py-1 text-[11px] uppercase tracking-wide"
                          data-testid={`venue-offer-ribbon-${venue.id}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" /> {venue.offer_badge || 'On Offer'}
                        </Badge>
                      )}
                    </div>
                    <SeatsBadge count={venue.total_capacity} className="absolute top-3 right-3" />
                  </div>

                  <CardContent className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-lg flex items-center gap-1.5 group-hover:text-primary transition-colors">
                      {venue.name}
                      {venue.region && (
                        <Info className="w-4 h-4 text-muted-foreground shrink-0" title={venue.region} />
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground flex items-start gap-1.5 mt-1">
                      <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
                      <span className="line-clamp-2">{venue.address || venue.city || 'Bangalore'}</span>
                    </p>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-muted-foreground">
                      {venueHighlights(venue).map(({ icon: Icon, label }, i) => (
                        <span key={i} className="flex items-center gap-1.5">
                          <Icon className="w-4 h-4 shrink-0" />{label}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between gap-3 mt-auto pt-4">
                      {fromRate ? (
                        <span className="text-xl font-bold">
                          {formatRate(fromRate.rate)}
                          <span className="text-sm font-normal text-muted-foreground ml-0.5">{fromRate.unit}</span>
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />{venue.opening_time || '08:00'} - {venue.closing_time || '22:00'}
                        </span>
                      )}
                      <Button size="sm" className="gap-1 group-hover:gap-2 transition-all">
                        View Spaces <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;
